<script setup lang="ts">
import {computed, nextTick, onMounted, onUnmounted, ref, watch} from "vue";
import {useModalStore} from "../../store/modal";
import {useRoute, useRouter} from "vue-router";
import {useChatStore} from "../../store/chat";
import ChatMessage from "../../components/chats/ChatMessage.vue";
import TicketInfoCard from "../../components/tickets/TicketInfoCard.vue";
import ChatMessageInput from "../../components/chats/ChatMessageInput.vue";
import {useAuthStore} from "../../store/auth";

const authStore = useAuthStore()
const modalStore = useModalStore()
const route = useRoute()
const store = useChatStore()
const messageCount = computed(() => {
    return store.chat ? store.chat?.messages?.length : 0;
})
const router = useRouter()
const chat = ref()
const showInfo = ref(false)
const contact = computed(() => chat.value?.contact ?? null);
const client = computed(() => contact.value ? contact.value?.client ?? null : null);
const order = computed(() => chat.value?.order ?? null);

watch(messageCount, async () => {
    if (!messageCount.value) return;
    await scrollToBottom();
}, {flush: "post"})

const scrollSection = ref<HTMLElement | null>(null)

const scrollToBottom = async () => {
    await nextTick();
    if (!scrollSection.value) return;
    scrollSection.value.scrollTop = scrollSection.value.scrollHeight;
};
const formatCreatedAt = (value?: string | null) => {
    if (!value) return "";

    const date = new Date(value);

    const dateFormatter = new Intl.DateTimeFormat("en-GB", {
        day: "2-digit",
        month: "long",
        year: "numeric",
    });

    const timeFormatter = new Intl.DateTimeFormat("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });

    return `${dateFormatter.format(date)}  •  ${timeFormatter.format(date)}`;
};

const completeChat = async () => {
    await store.completeChat(chat.value.id)
    chat.value = store.chat
}

const back = () => {
    router.back()
}

const createCall = () => {
    modalStore.open('CreateCall', {uuid: order.value?.uuid}, 'body', 'bottom-right')
}

const selectedTemplate = ref<string | null>(null)
const templateOptions = [
    {label: 'Request Customer Reply', value: 'reopen_customer_conversation'},
    {label: 'Request Banking Details', value: 'reseller_refund_request'},
]

watch(selectedTemplate, (val) => {
    if (!val) return
    modalStore.open('SendTemplateMessage', {template_name: val}, 'body', 'bottom-right')
    selectedTemplate.value = null
})

const setup = async () => {
    await store.getChat(route.params?.id)
    await authStore.getNotifications();
    chat.value = store.chat
    window.Echo.private(`chats.${chat.value?.id}`)
        .listen('ChatMessageReceived', (e) => {
            chat.value.messages.push(e.message)
        })
        .listen('ChatMessageStatusUpdate', (e) => {
            const index = chat.value.messages.findIndex(
                m => m.id === e.message.id
            )

            if (index !== -1) {
                chat.value.messages[index] = e.message
            }
        })
    await scrollToBottom();
}

watch(
    () => route.params.id,
    async (id) => {
        if (!id) return
        await setup()
    }
)

onMounted(async () => {
    await setup()
})

onUnmounted(() => {
    if (chat.value?.id) {
        window.Echo.leave(`private-chats.${chat.value.id}`)
    }
})
</script>

<template>
    <main class="chat-view">
        <section class="section-single-chat">
            <div class="section-chat-header" :class="{'--is-active': showInfo}">
                <div class="header-meta">
                    <button @click.prevent="back">
                        <ah-icon name="arrow-left"/>
                    </button>
                    <dl>
                        <dt>
                            {{
                                (client ? `${client.first_name} ${client.last_name} • ` : contact ? `${contact.profile_name} • ` : '') + chat?.phone_number
                            }}
                        </dt>
                    </dl>
                </div>
                <div class="header-actions">
                    <brand-button class="block-button" as="button" variant="small"
                                  @click.passive="modalStore.open('BlockChatContact', {contact: chat.contact}, 'body', 'bottom-right')">
                        <ah-icon name="cross-default"/>
                        {{ chat?.contact?.blocked_chat_contact ? 'Unblock' : 'Block' }} Client
                    </brand-button>

                    <BrandButton variant="small" v-if="order?.cart?.client_solutions" as="a"
                                 :href="`https://mojo.afrihost.com/en/manage/${order?.cart?.client_solutions?.find(s => s.product_type === 'solution_composite_fibre')?.id}`"
                                 target="_blank">
                        <ah-icon name="user-single"/>
                        Mojo Profile
                    </BrandButton>

                    <BrandButton variant="small" v-if="order?.cart?.client_solutions" as="button"
                                 @click="createCall()">
                        <ah-icon name="call-default"/>
                        Generate Call
                    </BrandButton>

                    <brand-button as="button" variant="small" @click="showInfo = !showInfo">
                        <ah-icon :name="showInfo ? 'cross-default' : 'info-circle-solid'"/>
                        Info
                    </brand-button>

                    <brand-form-select v-if="!chat?.completed_at"
                                       variant="small"
                                       class="auto-reply-dropdown"
                                       label="Send Template."
                                       :options="templateOptions"
                                       v-model="selectedTemplate"/>

                    <brand-button as="button" variant="small"
                                  @click="modalStore.open('CompleteChat', {id: chat.id}, 'body', 'bottom-right')"
                                  v-if="!chat?.completed_at">
                        <ah-icon name="check-all"/>
                        Complete
                    </brand-button>
                </div>
            </div>

            <div class="section-chat-messages" ref="scrollSection">
                <div class="chat-messages">
                    <chat-message :message="m" v-for="m in chat?.messages"/>
                </div>

                <Transition>
                    <aside class="ticket-info" v-if="showInfo">
                        <ticket-info-card icon="info-circle-solid"
                                          :title="`Created ${formatCreatedAt(chat?.created_at)}`"
                                          :sub-title="(client ? `${client.first_name} ${client.last_name} • ` : contact ? `${contact.profile_name} • ` : '') + chat?.phone_number"/>
                        <ticket-info-card icon="cart-default"
                                          title="Linked order."
                                          :sub-title="chat?.order.uuid"
                                          v-if="chat?.order"/>
                        <ticket-info-card icon="user-single"
                                          :title="`${chat.assignee.first_name} ${chat.assignee.last_name}`"
                                          :sub-title="chat.assignee.email"
                                          type="chat"
                                          v-if="chat?.assignee"/>

                        <div class="ticket-info-header">
                            <h2>Notes.</h2>
                            <a @click.passive="modalStore.open('ChatAddNote', {id: chat.id}, 'body', 'bottom-right')">Add
                                note.</a>
                        </div>
                        <ticket-info-card v-for="note in chat.notes" icon="paper-file"
                                          :title="`${note.created_by.first_name} ${note.created_by.last_name}`"
                                          :sub-title="note.created_at" :content="note.content"/>


                        <div class="ticket-info-header" v-if="chat.contact.chats">
                            <h2>Previous chats.</h2>
                        </div>

                        <RouterLink :to="`/chats/view/${c.id}`" v-for="c in chat.contact.chats" class="chat-link-row">
                            <ticket-info-card icon="speechbubble-sms"
                                              :title="c.uuid"
                                              :sub-title="c.completed_at ? `Completed at: ${c.completed_at}` : ''"
                            />
                        </RouterLink>
                    </aside>
                </Transition>
            </div>

            <div class="section-chat-response">
                <chat-message-input :chat-id="chat?.id" v-if="!chat?.completed_at"/>
            </div>
        </section>
    </main>
</template>

<style lang="scss">
.v-enter-active,
.v-leave-active {
    transition: opacity .3s ease-in;
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
}

main.chat-view {
    display: flex;
    justify-content: space-between;

    aside {
        width: 38rem;
        padding: 1rem;
        background: var(--color-grey-dew);
        border-radius: 2.4rem;
        overflow-y: auto;
        position: sticky;
        top: 0;
        right: 0;

        .chat-link-row {
            margin-bottom: 1rem;
            display: block;
        }

        @include dark() {
            background: #192D39;
        }
    }
}

.section-single-chat {
    display: grid;
    grid-template-rows: 8rem 1fr;
    grid-template-columns: 100%;
    transition: all .2s ease;

    width: 100%;

    &:has(.message-send-box.--is-focused) {
        grid-template-rows: 8rem 1fr 20rem;
    }
}

.section-chat-header {
    display: flex;
    justify-content: space-between;
    align-items: center;

    .header-meta {
        display: flex;
        align-items: center;
        gap: 2rem;

        button {
            width: 3.6rem;
            height: 3.6rem;
            border-radius: 1.8rem;
            border: none;
            background: #fff;
            display: flex;
            align-items: center;
            justify-content: center;

            .icon {
                fill: var(--color-grey-hazy);
            }
        }

        dt {
            @include font(2.2, 2.2, black);
            transition: .1s ease;
        }

        dd {
            @include font(1.6, 1.6, book);
            transition: .3s ease-in-out;
            margin-top: 1rem;
        }
    }

    .header-actions {
        display: flex;
        gap: 1rem;

        .brand-button {
            display: flex;
            gap: .4rem;
            align-items: center;
            transition: .1s ease-in-out;
            cursor: pointer;

            .icon {
                transition: .1s ease-in-out;
            }

            &:hover {
                background: var(--color-raspberry);
                color: var(--color-white);

                .icon {
                    fill: var(--color-white);
                }
            }

            &.block-button {
                background: var(--color-raspberry);
            }
        }

        .auto-reply-dropdown {
            min-width: 25rem;

            label {
                border: none !important;
            }

            .form-select-options {
                border: none!important;
            }
        }
    }

    &.--is-active {

        .header-meta {
            dt {
                @include font(3, 3, black);
            }

            dd {
                @include font(1.6, 1.6, book);
                margin-top: 1rem;
            }
        }

        .header-actions {
            .brand-button:first-child {
                background: var(--color-raspberry);
                color: var(--color-white);

                .icon {
                    fill: var(--color-white);
                }
            }
        }
    }

    @include dark() {
        .header-meta {
            button {
                background: var(--color-hazy);

                .icon {
                    fill: var(--color-dew);
                }
            }

            dl * {
                color: var(--color-white);
            }
        }

        .header-actions {
            button, a {
                .icon {
                    fill: var(--color-white);
                }
            }
        }
    }
}

.section-chat-messages {
    padding: 0 2rem;
    overflow-y: auto;
    display: flex;
    gap: 2rem;
    position: relative;

    .chat-messages {
        display: flex;
        flex-direction: column;
        gap: 1rem;
        flex: 1;
    }
}

.section-chat-response {
    display: grid;
    grid-template-columns: 1fr 13rem;
    gap: 1rem;
    padding: 2rem 0 0;

    .brand-button {
        height: 6rem;
        padding: 0 2rem;
        display: flex;
        justify-content: center;
        gap: 1rem;
        align-items: center;
        fill: #fff;

        .label {
            font-weight: var(--font-weight-black);
        }
    }
}

.chat-info-header {
    padding: 2rem;
    display: flex;
    justify-content: space-between;
    align-items: center;

    a {
        @include font(1.1, null, black);
        color: var(--color-tiffany-dark);
        cursor: pointer;
    }
}
</style>
